import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Globe, FolderOpen,
  Settings, FileText, Clock, BarChart3, Pencil, Trash2, Play,
} from 'lucide-react';
import { useAgent, useUpdateAgent, useDeleteAgent } from '../hooks/useAgents';
import { useInstances } from '../hooks/useInstances';
import { getRoleConfig } from '../components/AgentCard';
import AgentForm, { type AgentFormData } from '../components/AgentForm';
import InstanceLaunchModal from '../components/InstanceLaunchModal';

type Tab = 'config' | 'prompt' | 'history' | 'stats';

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const STATUS_COLORS: Record<string, string> = {
  running: 'bg-green-500',
  paused: 'bg-yellow-500',
  completed: 'bg-slate-400',
  stopped: 'bg-slate-400',
  failed: 'bg-red-500',
  pending: 'bg-blue-400',
  starting: 'bg-blue-500',
};

export default function AgentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: agent, isLoading } = useAgent(id);
  const updateAgent = useUpdateAgent();
  const deleteAgent = useDeleteAgent();
  const [tab, setTab] = useState<Tab>('config');
  const [formOpen, setFormOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);

  // Get instances for the agent's project to compute stats
  const { data: instances } = useInstances(agent?.projectId ?? undefined);
  const agentInstances = instances?.filter(i => i.agentId === id) ?? [];

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-400">Loading...</div>;
  }

  if (!agent) {
    return <div className="flex h-64 items-center justify-center text-sm text-slate-500">Agent not found</div>;
  }

  const cfg = getRoleConfig(agent.role);
  const Icon = cfg.icon;
  const ScopeIcon = agent.scope === 'global' ? Globe : FolderOpen;

  const handleUpdate = async (data: AgentFormData) => {
    await updateAgent.mutateAsync({ id: agent.id, ...data });
  };

  const handleDelete = async () => {
    if (!confirm(`Delete agent "${agent.name}"? This cannot be undone.`)) return;
    await deleteAgent.mutateAsync(agent.id);
    navigate(-1);
  };

  // Stats
  const totalInstances = agentInstances.length;
  const completedInstances = agentInstances.filter(i => i.status === 'completed').length;
  const failedInstances = agentInstances.filter(i => i.status === 'failed').length;
  const totalCost = agentInstances.reduce((sum, i) => sum + Number(i.totalCostUsd), 0);
  const avgTurns = totalInstances > 0
    ? Math.round(agentInstances.reduce((sum, i) => sum + i.currentTurn, 0) / totalInstances)
    : 0;
  const successRate = totalInstances > 0
    ? Math.round((completedInstances / totalInstances) * 100)
    : 0;

  const tabs: { key: Tab; label: string; icon: typeof Settings }[] = [
    { key: 'config', label: 'Config', icon: Settings },
    { key: 'prompt', label: 'System Prompt', icon: FileText },
    { key: 'history', label: 'History', icon: Clock },
    { key: 'stats', label: 'Stats', icon: BarChart3 },
  ];

  return (
    <div className="mx-auto max-w-5xl p-6">
      {/* Back */}
      <button
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </button>

      {/* Header */}
      <div className="mb-6 flex items-start justify-between">
        <div className="flex items-center gap-4">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${cfg.bg}`}>
            <Icon className={`h-6 w-6 ${cfg.color}`} />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{agent.name}</h1>
            <div className="mt-1 flex items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${cfg.badge}`}>
                {agent.role}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                <ScopeIcon className="h-3.5 w-3.5" />
                {agent.scope}
              </span>
              {agent.defaultModel && (
                <span className="text-xs text-slate-400">{agent.defaultModel}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setLaunchOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-sm text-white hover:bg-slate-800"
          >
            <Play className="h-3.5 w-3.5" />
            Launch Instance
          </button>
          <button
            onClick={() => setFormOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
          >
            <Pencil className="h-3.5 w-3.5" />
            Edit
          </button>
          <button
            onClick={handleDelete}
            className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 border-b border-slate-200">
        {tabs.map(({ key, label, icon: TabIcon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === key
                ? 'border-slate-900 text-slate-900'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <TabIcon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Config Tab */}
      {tab === 'config' && (
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <h3 className="mb-3 text-sm font-semibold text-slate-700">Configuration</h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div>
                <span className="text-slate-400">Role</span>
                <p className="font-medium text-slate-900 capitalize">{agent.role}</p>
              </div>
              <div>
                <span className="text-slate-400">Scope</span>
                <p className="font-medium text-slate-900 capitalize">{agent.scope}</p>
              </div>
              <div>
                <span className="text-slate-400">Default Model</span>
                <p className="font-medium text-slate-900">{agent.defaultModel || 'System default'}</p>
              </div>
              <div>
                <span className="text-slate-400">Max Turns</span>
                <p className="font-medium text-slate-900">{agent.maxTurnsDefault ?? 50}</p>
              </div>
              <div>
                <span className="text-slate-400">Max Budget</span>
                <p className="font-medium text-slate-900">${agent.maxBudgetDefault ?? '5.00'}</p>
              </div>
              <div>
                <span className="text-slate-400">Working Directory</span>
                <p className="font-mono text-xs text-slate-900">{agent.defaultCwd || 'None'}</p>
              </div>
            </div>
          </div>
          {agent.allowedTools && agent.allowedTools.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-700">Allowed Tools</h3>
              <div className="flex flex-wrap gap-2">
                {agent.allowedTools.map(tool => (
                  <span key={tool} className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-700">
                    {tool}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Prompt Tab */}
      {tab === 'prompt' && (
        <div className="rounded-lg border border-slate-200">
          {agent.systemPrompt ? (
            <pre className="overflow-x-auto rounded-lg bg-slate-900 p-6 font-mono text-sm leading-relaxed text-slate-100">
              {agent.systemPrompt}
            </pre>
          ) : (
            <div className="p-8 text-center text-sm text-slate-400">No system prompt defined</div>
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="rounded-lg border border-slate-200 bg-white">
          {agentInstances.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-400">No instances yet</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Task / Prompt</th>
                  <th className="px-4 py-3 font-medium">Model</th>
                  <th className="px-4 py-3 font-medium">Turns</th>
                  <th className="px-4 py-3 font-medium">Cost</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {agentInstances
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map(inst => (
                    <tr
                      key={inst.id}
                      className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50"
                      onClick={() => navigate(`/instances/${inst.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className={`h-2 w-2 rounded-full ${STATUS_COLORS[inst.status] || 'bg-slate-400'}`} />
                          <span className="capitalize">{inst.status}</span>
                        </div>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-3 text-slate-600">
                        {inst.task?.title ?? inst.prompt?.slice(0, 60) ?? 'No prompt'}
                      </td>
                      <td className="px-4 py-3 text-slate-500">{inst.model}</td>
                      <td className="px-4 py-3 text-slate-500">{inst.currentTurn}/{inst.maxTurns}</td>
                      <td className="px-4 py-3 text-slate-500">${Number(inst.totalCostUsd).toFixed(2)}</td>
                      <td className="px-4 py-3 text-slate-400">{relativeTime(inst.createdAt)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {tab === 'stats' && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-xs text-slate-500">Total Instances</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{totalInstances}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-xs text-slate-500">Total Cost</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">${totalCost.toFixed(2)}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-xs text-slate-500">Avg Turns</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{avgTurns}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-5">
            <p className="text-xs text-slate-500">Success Rate</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{successRate}%</p>
            <p className="mt-0.5 text-xs text-slate-400">{completedInstances} completed, {failedInstances} failed</p>
          </div>
        </div>
      )}

      {/* Edit Form */}
      <AgentForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleUpdate}
        agent={agent}
        isPending={updateAgent.isPending}
      />

      {/* Quick Launch Modal */}
      {launchOpen && (
        <InstanceLaunchModal
          open={launchOpen}
          onClose={() => setLaunchOpen(false)}
          projectId={agent.projectId ?? undefined}
          initialAgentId={agent.id}
        />
      )}
    </div>
  );
}
